// frontend/src/pages/ArchivePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTopics, getBrandVoices } from '../services/api';
import api from '../services/api';
import { Archive, Trash2, RotateCcw, Loader, Filter, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

const VOICE_COLORS = [
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
  'bg-pink-100 text-pink-700',
  'bg-amber-100 text-amber-700',
  'bg-cyan-100 text-cyan-700',
  'bg-violet-100 text-violet-700',
];

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const ArchivePage = () => {
  const [content, setContent] = useState([]);
  const [topics, setTopics] = useState([]);
  const [brandVoices, setBrandVoices] = useState([]);
  const [summary, setSummary] = useState([]); // [{year, month, count}]
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  // Filters for viewing
  const [filters, setFilters] = useState({
    topic_id: '',
    content_type: '',
    year: '',
    month: '',
  });

  // Bulk delete selections
  const [bulkFilters, setBulkFilters] = useState({
    year: '',
    month: '',
    topic_id: '',
    content_type: '',
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadArchive();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    try {
      const [topicsRes, voicesRes, summaryRes] = await Promise.all([
        getTopics(),
        getBrandVoices(),
        api.get('/api/content/archive/summary')
      ]);
      setTopics(topicsRes.data);
      setBrandVoices(voicesRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to load archive data', err);
    }
    await loadArchive();
    setLoading(false);
  };

  const loadArchive = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.topic_id) params.append('topic_id', filters.topic_id);
      if (filters.content_type) params.append('content_type', filters.content_type);
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);
      const res = await api.get(`/api/content/archive?${params.toString()}`);
      setContent(res.data);
    } catch (err) {
      console.error('Failed to load archive', err);
    }
  };

  const handleRestore = async (id, title) => {
    try {
      await api.patch(`/api/content/${id}/restore`);
      setContent(content.filter(c => c.id !== id));
      setSuccess(`"${title}" restored to Content Library`);
      setTimeout(() => setSuccess(''), 3000);
      // Refresh summary
      const summaryRes = await api.get('/api/content/archive/summary');
      setSummary(summaryRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to restore content');
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Permanently delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/content/${id}`);
      setContent(content.filter(c => c.id !== id));
      setSuccess(`"${title}" permanently deleted`);
      setTimeout(() => setSuccess(''), 3000);
      const summaryRes = await api.get('/api/content/archive/summary');
      setSummary(summaryRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete content');
    }
  };

  const handleBulkDelete = async () => {
    try {
      const params = new URLSearchParams();
      if (bulkFilters.year) params.append('year', bulkFilters.year);
      if (bulkFilters.month) params.append('month', bulkFilters.month);
      if (bulkFilters.topic_id) params.append('topic_id', bulkFilters.topic_id);
      if (bulkFilters.content_type) params.append('content_type', bulkFilters.content_type);
      const res = await api.delete(`/api/content/archive/bulk?${params.toString()}`);
      setSuccess(res.data.detail);
      setBulkDeleteConfirm(false);
      setShowBulkDelete(false);
      setBulkFilters({ year: '', month: '', topic_id: '', content_type: '' });
      setTimeout(() => setSuccess(''), 4000);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const formatContentType = (type) => {
    const labels = {
      linkedin_post: 'LinkedIn Post',
      white_paper: 'White Paper',
      report: 'Report',
      article: 'Article',
      policy_brief: 'Policy Brief'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      linkedin_post: 'bg-blue-100 text-blue-700',
      white_paper: 'bg-purple-100 text-purple-700',
      report: 'bg-green-100 text-green-700',
      article: 'bg-orange-100 text-orange-700',
      policy_brief: 'bg-red-100 text-red-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getTopicName = (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    return topic ? topic.name : 'Unknown';
  };

  const getBrandVoiceLabel = (voiceName) => {
    const voice = brandVoices.find(v => v.name === voiceName);
    return voice ? (voice.description || voice.name) : voiceName;
  };

  const getBrandVoiceColor = (voiceName) => {
    const idx = brandVoices.findIndex(v => v.name === voiceName);
    return idx >= 0 ? VOICE_COLORS[idx % VOICE_COLORS.length] : 'bg-gray-100 text-gray-500';
  };

  // Group content by year+month
  const grouped = content.reduce((acc, item) => {
    const date = new Date(item.archived_at || item.created_at);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = `${MONTH_NAMES[date.getMonth() + 1]} ${date.getFullYear()}`;
    if (!acc[key]) acc[key] = { label, items: [] };
    acc[key].items.push(item);
    return acc;
  }, {});

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Available years from summary
  const availableYears = [...new Set(summary.map(s => s.year))].sort((a, b) => b - a);
  const availableMonths = bulkFilters.year
    ? summary.filter(s => s.year === parseInt(bulkFilters.year)).map(s => s.month)
    : [];

  const bulkDeleteCount = bulkFilters.year || bulkFilters.month || bulkFilters.topic_id || bulkFilters.content_type
    ? content.filter(item => {
        const date = new Date(item.archived_at || item.created_at);
        if (bulkFilters.year && date.getFullYear() !== parseInt(bulkFilters.year)) return false;
        if (bulkFilters.month && (date.getMonth() + 1) !== parseInt(bulkFilters.month)) return false;
        if (bulkFilters.topic_id && item.topic_id !== bulkFilters.topic_id) return false;
        if (bulkFilters.content_type && item.content_type !== bulkFilters.content_type) return false;
        return true;
      }).length
    : content.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark">Archive</h1>
          <p className="text-gray-500 mt-1">
            {content.length} archived item{content.length !== 1 ? 's' : ''} — restore or permanently delete
          </p>
        </div>
        <button
          onClick={() => { setShowBulkDelete(!showBulkDelete); setBulkDeleteConfirm(false); }}
          className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 cursor-pointer transition-colors"
        >
          <Trash2 size={18} />
          Bulk Delete
        </button>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6 flex items-center gap-2">
          <Archive size={18} />
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center gap-2">
          <AlertTriangle size={18} />
          {error}
        </div>
      )}

      {/* Bulk Delete Panel */}
      {showBulkDelete && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6">
          <h3 className="text-md font-semibold text-red-700 mb-3 flex items-center gap-2">
            <Trash2 size={18} />
            Bulk Delete — Permanently removes matching archived content
          </h3>
          <p className="text-sm text-red-600 mb-4">
            Leave all filters blank to delete everything in the archive. This cannot be undone.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
              <select
                value={bulkFilters.year}
                onChange={(e) => setBulkFilters({ ...bulkFilters, year: e.target.value, month: '' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
              <select
                value={bulkFilters.month}
                onChange={(e) => setBulkFilters({ ...bulkFilters, month: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                disabled={!bulkFilters.year}
              >
                <option value="">All months</option>
                {availableMonths.map(m => <option key={m} value={m}>{MONTH_NAMES[m]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Topic</label>
              <select
                value={bulkFilters.topic_id}
                onChange={(e) => setBulkFilters({ ...bulkFilters, topic_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All topics</option>
                {topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Content Type</label>
              <select
                value={bulkFilters.content_type}
                onChange={(e) => setBulkFilters({ ...bulkFilters, content_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">All types</option>
                <option value="linkedin_post">LinkedIn Post</option>
                <option value="white_paper">White Paper</option>
                <option value="report">Report</option>
                <option value="article">Article</option>
                <option value="policy_brief">Policy Brief</option>
              </select>
            </div>
          </div>

          {!bulkDeleteConfirm ? (
            <button
              onClick={() => setBulkDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 cursor-pointer transition-colors"
            >
              <Trash2 size={16} />
              Delete {bulkDeleteCount} item{bulkDeleteCount !== 1 ? 's' : ''}
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-sm font-medium text-red-700">
                Are you sure? {bulkDeleteCount} item{bulkDeleteCount !== 1 ? 's' : ''} will be permanently deleted.
              </p>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 cursor-pointer transition-colors text-sm font-medium"
              >
                Yes, delete permanently
              </button>
              <button
                onClick={() => setBulkDeleteConfirm(false)}
                className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* View Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-gray-500">
            <Filter size={18} />
            <span className="text-sm font-medium">Filter view:</span>
          </div>
          <select
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: e.target.value, month: '' })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All years</option>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select
            value={filters.month}
            onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            disabled={!filters.year}
          >
            <option value="">All months</option>
            {summary.filter(s => !filters.year || s.year === parseInt(filters.year)).map(s => (
              <option key={s.month} value={s.month}>{MONTH_NAMES[s.month]}</option>
            ))}
          </select>
          <select
            value={filters.topic_id}
            onChange={(e) => setFilters({ ...filters, topic_id: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All topics</option>
            {topics.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select
            value={filters.content_type}
            onChange={(e) => setFilters({ ...filters, content_type: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All types</option>
            <option value="linkedin_post">LinkedIn Post</option>
            <option value="white_paper">White Paper</option>
            <option value="report">Report</option>
            <option value="article">Article</option>
            <option value="policy_brief">Policy Brief</option>
          </select>
          {(filters.year || filters.month || filters.topic_id || filters.content_type) && (
            <button
              onClick={() => setFilters({ topic_id: '', content_type: '', year: '', month: '' })}
              className="text-sm text-brand-mid hover:text-brand-dark cursor-pointer"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Content Grouped by Month */}
      {Object.keys(grouped).length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Archive size={40} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No archived content found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped)
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([key, group]) => (
              <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Group Header */}
                <div
                  className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleGroup(key)}
                >
                  <div className="flex items-center gap-3">
                    <Archive size={18} className="text-gray-400" />
                    <h3 className="font-semibold text-gray-700">{group.label}</h3>
                    <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
                      {group.items.length} item{group.items.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {collapsedGroups[key]
                    ? <ChevronDown size={18} className="text-gray-400" />
                    : <ChevronUp size={18} className="text-gray-400" />
                  }
                </div>

                {/* Group Items */}
                {!collapsedGroups[key] && (
                  <div className="divide-y divide-gray-100">
                    {group.items.map((item) => (
                      <div key={item.id} className="p-4 flex items-start justify-between hover:bg-gray-50 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeColor(item.content_type)}`}>
                              {formatContentType(item.content_type)}
                            </span>
                            <span className="text-xs text-gray-500">{getTopicName(item.topic_id)}</span>
                            {item.brand_voice && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getBrandVoiceColor(item.brand_voice)}`}>
                                {getBrandVoiceLabel(item.brand_voice)}
                              </span>
                            )}
                            <span className="text-xs text-gray-400">
                              Archived {new Date(item.archived_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p
                            className="text-sm font-medium text-gray-800 cursor-pointer hover:text-brand-mid"
                            onClick={() => navigate(`/content/${item.id}`)}
                          >
                            {item.title || 'Untitled'}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {item.content ? item.content.trim().split(/\s+/).length.toLocaleString() : 0} words
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleRestore(item.id, item.title)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs border border-brand-mid text-brand-mid rounded-lg hover:bg-brand-light cursor-pointer transition-colors"
                            title="Restore to Content Library"
                          >
                            <RotateCcw size={14} />
                            Restore
                          </button>
                          <button
                            onClick={() => handleDelete(item.id, item.title)}
                            className="p-1.5 text-gray-300 hover:text-red-500 cursor-pointer transition-colors"
                            title="Permanently delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ArchivePage;
